import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Spinner, Wrapper } from '@/components';
import { useAuth, useAuthUser } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { fixUrlForCloudFront } from '@/utils/cloudfront';
import { updateUserInStorage } from '@/utils/auth';
import { NavigationAbortError } from '@/utils/errors';

import { AccountsSection } from './sections/AccountsSection';
import { EmailSection } from './sections/EmailSection';
import { FormDataType, GeneralSection } from './sections/GeneralSection';
import { NotificationsSection } from './sections/NotificationsSection';
import { PayoutSection } from './sections/PayoutSection';
import { ShippingSection } from './sections/ShippingSection';
import { TermsSection } from './sections/TermsSection';
import styles from './Settings.module.css';
import PreventionModal from './modals/PreventionModal';
import { compareKeysAndValues } from './utils';

export function Settings() {
  const [showPopup, setShowPopup] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const [generalForm, setGeneralForm] = useState<null | FormDataType>(null);
  const [shippingForm, setShippingForm] = useState<null | Api.ShippingAddress>(null);

  const { t } = useTranslation();
  const { authData, setAuthData } = useAuth();
  const user = useAuthUser();

  const generalFormRef = useRef<HTMLDivElement>(null);
  const shippingFormRef = useRef<HTMLDivElement>(null);

  function updateUser(userData: Api.UserObject) {
    updateUserInStorage(userData);
    setAuthData({ ...authData, user: userData });
  }

  const router = useRouter();

  const handleRouteChange = (url: string | null) => {
    if ((generalForm || shippingForm) && user && router.route !== url) {
      if (
        generalForm &&
        !compareKeysAndValues(
          {
            first_name: user.first_name,
            last_name: user.last_name,
            phone: user.phone,
            language: user.language
          },
          generalForm
        )
      ) {
        setShowPopup(true);
        setPendingRoute(url);
        router.events.emit('routeChangeError');
        console.log(`Route change to "${url}" was aborted - showing confirmation modal.`);
        // Throw an error to actually prevent the route change
        throw new NavigationAbortError('Route change aborted by user confirmation dialog');
      } else if (
        shippingForm &&
        !compareKeysAndValues(
          shippingForm,
          user.influencer?.shipping_address as Api.ShippingAddress
        )
      ) {
        setShowPopup(true);
        setPendingRoute(url);
        router.events.emit('routeChangeError');
        console.log(`Route change to "${url}" was aborted - showing confirmation modal.`);
        // Throw an error to actually prevent the route change
        throw new NavigationAbortError('Route change aborted by user confirmation dialog');
      }
    }
  };

  const handleConfirm = () => {
    setShowPopup(false);
    setGeneralForm(null);
    setShippingForm(null);
    router.push(fixUrlForCloudFront(pendingRoute as string));
  };

  const handleCancel = () => {
    setShowPopup(false);
    setPendingRoute(null);
    if (generalForm && generalFormRef.current) {
      generalFormRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (shippingForm && shippingFormRef.current) {
      shippingFormRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [generalForm, shippingForm, router]);

  if (!user || !user.user_id) {
    return (
      <div className={styles.loader}>
        <Spinner hasWrapper />
      </div>
    );
  }

  return (
    <Wrapper heading={t('account.heading')}>
      <EmailSection user={user} onUpdate={updateUser} />
      <div ref={generalFormRef}>
        <GeneralSection user={user} onUpdate={updateUser} onFormChange={setGeneralForm} />
      </div>
      {user.is_client && <NotificationsSection user={user} onUpdate={updateUser} />}
      {user.is_influencer && (
        <>
          <AccountsSection user={user} onUpdate={updateUser} />
          <div ref={shippingFormRef}>
            <ShippingSection user={user} onUpdate={updateUser} onFormChange={setShippingForm} />
          </div>
          {!user.is_agent && <PayoutSection user={user} onUpdate={updateUser} />}
          <TermsSection />
        </>
      )}
      {showPopup && (
        <PreventionModal
          onClose={() => setShowPopup(false)}
          onBack={handleCancel}
          onDiscard={handleConfirm}
        />
      )}
    </Wrapper>
  );
}
